<template>
    <div ref='mapboxMapDiv'></div>
</template>



<script>
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

var MapboxDraw = require('@mapbox/mapbox-gl-draw');
var turfBBox = require('@turf/bbox').default;
var turfArea = require('@turf/area').default;

import missingParcelImage from '../../assets/img/missing-parcel-marker.png';
import woodlandPinImage from '../../assets/img/woodland-marker.png';

export default {
    components: {
    },
    props: {
        // mapbox requires an access token
        'access-token': {
            type: String,
            required: false,
            default: 'pk.eyJ1Ijoid29vZHNjYW1wLWNvbGlubiIsImEiOiJjamxnd3VycDgxYzZyM3dyMG91ZXFkNXN0In0.dQpj9Uxzn_Jq_AXwUHloMw'
        },
        // target map style, you can also load a local map style configuration
        'map-style': {
            type: [String, Object],
            default: 'mapbox://styles/mapbox/satellite-streets-v10'
        },
        // whether to display the attribution control
        // this is required by mapbox unless you fulfill this requirement elsehow
        'attribution-control': {
            type: Boolean,
            default: true
        },
        // whether map can be interacted with
        interactive: {
            type: Boolean,
            default: true
        },
        // whether to instance the geocoder
        useGeocoder: {
            type: Boolean,
            default: false
        },
        // whether to jump, ease, or fly for transitions
        transitionMode: {
            type: String,
            required: false,
            default: 'jump',
            validator: function (value) {
                return ['jump', 'ease', 'fly'].indexOf(value) !== -1;
            }
        },
        // longitude (dynamic)
        lng: {
            type: [Number, String],
            required: false,
            default: -84.547936
        },
        // latitude (dynamic)
        lat: {
            type: [Number, String],
            required: false,
            default: 32.234294
        },
        // zoom (dynamic)
        zoom: {
            type: [Number, String],
            default: 6
        },
        // pitch (dynamic)
        pitch: {
            type: [Number, String],
            default: 0
        },
        // bearing (dynamic)
        bearing: {
            type: [Number, String],
            default: 0
        },

        parcelStrokeColour: {
            type: String,
            default: '#a3ca55'
        },

        parcelHighlightStrokeColour: {
            type: String,
            default: '#f4c842'
        },

        // Drawing Configuration
        allowDrawing: {
            default: true,
            type: Boolean
        },
        drawingGeoJSON: {
            default: null,
            type: Object
        },

        // Map highlights
        parcels: {
            default: null,
            type: Array
        },
        missingParcels: {
            type: Array
        },
        centroids: {
            type: Array
        }
    },
    data () {
        return {
            map: null,
            drawObject: null,
            geocoder: null,

            overFeatureId: '',
            popup: null
        };
    },
    mounted () {
        if (this.accessToken) {
            mapboxgl.accessToken = this.accessToken;
        } else {
            console.warn('NOTE -> No access token has been provided. If using Mapbox hosted tiles then this omission may break your map.');
        }
        this.map = new mapboxgl.Map({
            container: this.$refs.mapboxMapDiv,
            style: this.mapStyle,
            interactive: this.interactive,
            center: [
                this.lng,
                this.lat
            ],
            zoom: this.zoom,
            bearing: this.bearing,
            pitch: this.pitch,
            attributionControl: this.attributionControl,
            preserveDrawingBuffer: true
        });

        // return the map for reference from parent component
        this.map.on('load', () => { this.setMap(this.map); });
        this.map.on('remove', () => { this.unsetMap(); });
    },
    computed: {
        mapScene () {
            return {
                center: [this.lng, this.lat],
                zoom: this.zoom,
                bearing: this.bearing,
                pitch: this.pitch
            };
        }
    },
    watch: {
        mapStyle: {
            deep: true,
            handler (val) {
                if (val && this.map) {
                    // TODO: currently custom data layers are lost, but probably option to save them in future per
                    // https://github.com/mapbox/mapbox-gl-js/issues/4006
                    // currently this means that updating the style will undo any custom feature collections...
                    this.map.setStyle(val);
                } else {
                    console.error(`NOTE -> Unable to update map style to ${val}.`);
                }
            }
        },
        mapScene (val) {
            if (val) {
                if (!this.map) {
                    console.error('NOTE -> Map object not set');
                    return;
                }
                if (val.pitch < 0 || val.pitch > 60) {
                    console.error(`NOTE -> Unable to update pitch to ${val.pitch}. Exceeds permitted range.`);
                    return;
                }
                if (val.bearing < 0 || val.bearing > 360) {
                    console.error(`NOTE -> Unable to update bearing to ${val.bearing}. Exceeds permitted range.`);
                    return;
                }
                if (this.transitionMode === 'jump') {
                    this.map.jumpTo(val);
                } else if (this.transitionMode === 'ease') {
                    this.map.easeTo(val);
                } else if (this.transitionMode === 'fly') {
                    this.map.flyTo(val);
                }
            }
        },

        missingParcels: function () {
            this.renderMissingParcels();
        },

        parcels: function () {
            this.renderParcels();
        },

        drawingGeoJSON: {
            handler () {
                if (this.drawObject) {
                    // Refresh the drawing.  This delete and add is necessary for colour changes

                    // Check for changes
                    let a = JSON.stringify(this.drawObject.getAll());
                    let b = JSON.stringify(this.drawingGeoJSON);

                    if (a !== b) {
                        // console.log("changes detected: \n" + a + "\n" + b);
                        this.drawObject.deleteAll();
                        this.drawObject.add(this.drawingGeoJSON);
                    }

                    // (otherwise no changes needed)
                }
            },
            deep: true
        }
    },

    methods: {
        setMap (map) {
            this.map = map;

            this.map.setMaxZoom(16.9);

            // Setup the geocoder, hidden using CSS
            {
                this.geocoder = new MapboxGeocoder({
                    accessToken: mapboxgl.accessToken,
                    country: 'us',
                    bbox: [-89.121094, 29.458731, -82.792969, 35.245619]
                });

                this.geocoder.on('results', e => {
                    if (e.features && e.features.length == 0) {
                        this.$toasted.error('Cound not geolocate address.');
                    }
                });

                this.geocoder.on('error', e => {
                    this.$toasted.error('Unknown error');
                });

                this.map.addControl(this.geocoder);
            }

            // Add navigation controls
            if (this.interactive) {
                let nav = new mapboxgl.NavigationControl();
                map.addControl(nav, 'top-right');
            }

            {
                // sitkcing this here
                // map.on('mouseenter' , 'mouseleave')
                map.on('mouseenter', 'certpilot-highlighted', this.onMouseEnter_highlighted);
                map.on('mouseenter', 'missing-parcels', this.onMouseEnter_missing);
                map.on('mouseenter', 'woodland-centroids', this.onMouseEnter_centroids);

                map.on('mouseleave', 'certpilot-highlighted', this.onMouseLeave_highlighted);
                map.on('mouseleave', 'missing-parcels', this.onMouseLeave_missing);
                map.on('mouseleave', 'woodland-centroids', this.onMouseLeave_centroids);

                map.on('click', 'certpilot-highlighted', this.onMapClick_highlighted);
                map.on('click', 'missing-parcels', this.onMapClick_missing);
                map.on('click', 'woodland-centroids', this.onMapClick_centroids);
            }

            // Add [missing]parcel layer
            {
                var tileserver = 'https://tiles.woodscamp.com:8089/certpilot/{z}/{x}/{y}.pbf';

                // Add the source to query. In this example we're using
                // county polygons uploaded as vector tiles
                map.addSource('parcels', {
                    type: 'vector',
                    tiles: [tileserver]
                });

                // /////////////////////////////////////
                // Resting parcels
                // /////////////////////////////////////
                map.addLayer({
                    id: 'certpilot',
                    type: 'fill',
                    source: 'parcels',
                    'source-layer': 'certpilot',
                    paint: {
                        'fill-color': this.parcelStrokeColour,
                        'fill-opacity': 0
                    },
                    'minzoom': 10,
                    'maxzoom': 17
                });
                map.addLayer({
                    'id': 'certpilot-lines',
                    'type': 'line',
                    source: 'parcels',
                    'source-layer': 'certpilot',
                    'paint': {
                        'line-color': this.parcelStrokeColour,
                        'line-width': 1,
                        'line-opacity': 0.6
                    },
                    'minzoom': 10,
                    'maxzoom': 17
                });

                // /////////////////////////////////////
                // Mouseover parcels
                // /////////////////////////////////////
                map.addLayer({
                    id: 'certpilot-almost-highlighted',
                    type: 'fill',
                    source: 'parcels',
                    'source-layer': 'certpilot',
                    paint: {
                        'fill-outline-color': this.parcelStrokeColour,
                        'fill-color': this.parcelStrokeColour,
                        'fill-opacity': 0.25
                    },
                    'minzoom': 10,
                    'maxzoom': 17,
                    filter: ['in', 'id', '']
                });

                // /////////////////////////////////////
                // Selected parcels
                // /////////////////////////////////////
                map.addLayer({
                    id: 'certpilot-highlighted',
                    type: 'fill',
                    source: 'parcels',
                    'source-layer': 'certpilot',
                    paint: {
                        'fill-outline-color': this.parcelHighlightStrokeColour,
                        'fill-color': this.parcelHighlightStrokeColour,
                        'fill-opacity': 0.15
                    },
                    'minzoom': 10,
                    'maxzoom': 17,
                    filter: ['in', 'id', '']
                });
                map.addLayer({
                    'id': 'certpilot-highlighted-lines',
                    'type': 'line',
                    source: 'parcels',
                    'source-layer': 'certpilot',
                    'paint': {
                        'line-color': this.parcelHighlightStrokeColour,
                        'line-width': 3,
                        'line-opacity': 1
                    },
                    'minzoom': 10,
                    'maxzoom': 17,
                    filter: ['in', 'id', '']
                });

                // /////////////////////////////////////
                // Missing parcel source
                // /////////////////////////////////////
                map.addSource('missing-parcels', {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: []
                    }
                });
                map.loadImage(missingParcelImage, function (error, image) {
                    if (error) throw error;
                    map.addImage('missing-parcel', image);

                    map.addLayer({
                        id: 'missing-parcels',
                        type: 'symbol',
                        source: 'missing-parcels',
                        layout: {
                            'icon-image': 'missing-parcel',
                            'icon-size': 0.4
                        }
                    });
                });

                // /////////////////////////////////////
                // Parcel centroid source
                // /////////////////////////////////////
                map.addSource('woodland-centroids', {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: []
                    }
                });
                map.loadImage(woodlandPinImage, function (error, image) {
                    if (error) throw error;
                    map.addImage('woodland-pin', image);

                    map.addLayer({
                        id: 'woodland-centroids',
                        type: 'symbol',
                        source: 'woodland-centroids',
                        layout: {
                            'icon-image': 'woodland-pin',
                            'icon-size': 0.4
                        }
                    });
                });
            }

            // Add draw tools
            {
                // https://bl.ocks.org/dnseminara/0790e53cef9867e848e716937727ab18
                this.drawObject = new MapboxDraw({
                    userProperties: true,
                    touchEnabled: this.allowDrawing,
                    controls: {
                        point: false,
                        line_string: false,
                        polygon: this.allowDrawing,
                        trash: this.allowDrawing,
                        combine_features: false,
                        uncombine_features: false
                    },

                    styles: [
                        {
                            id: 'gl-draw-polygon-fill-inactive',
                            type: 'fill',
                            filter: [
                                'all',
                                // ["==", "active", "false"],
                                ['==', '$type', 'Polygon']
                                //  ["!=", "mode", "static"]
                            ],
                            paint: {
                                'fill-color': ['get', 'user_colour'],
                                'fill-outline-color': '#3bb2d0',
                                'fill-opacity': ['case', ['==', ['get', 'user_colour'], ''], 0, 0.25]
                            }
                        },
                        {
                            id: 'gl-draw-polygon-fill-active',
                            type: 'fill',
                            filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
                            paint: {
                                'fill-color': '#fbb03b',
                                'fill-outline-color': '#fbb03b',
                                'fill-opacity': 0.1
                            }
                        },
                        {
                            id: 'gl-draw-polygon-midpoint',
                            type: 'circle',
                            filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
                            paint: {
                                'circle-radius': 3,
                                'circle-color': '#fbb03b'
                            }
                        },
                        {
                            id: 'gl-draw-polygon-stroke-inactive',
                            type: 'line',
                            filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
                            layout: {
                                'line-cap': 'round',
                                'line-join': 'round'
                            },
                            paint: {
                                'line-color': '#3bb2d0',
                                'line-width': 2
                            }
                        },
                        {
                            id: 'gl-draw-polygon-stroke-active',
                            type: 'line',
                            filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
                            layout: {
                                'line-cap': 'round',
                                'line-join': 'round'
                            },
                            paint: {
                                'line-color': '#fbb03b',
                                'line-dasharray': [0.2, 2],
                                'line-width': 2
                            }
                        },
                        {
                            id: 'gl-draw-line-inactive',
                            type: 'line',
                            filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
                            layout: {
                                'line-cap': 'round',
                                'line-join': 'round'
                            },
                            paint: {
                                'line-color': '#3bb2d0',
                                'line-width': 2
                            }
                        },
                        {
                            id: 'gl-draw-line-active',
                            type: 'line',
                            filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']],
                            layout: {
                                'line-cap': 'round',
                                'line-join': 'round'
                            },
                            paint: {
                                'line-color': '#fbb03b',
                                'line-dasharray': [0.2, 2],
                                'line-width': 2
                            }
                        },
                        {
                            id: 'gl-draw-polygon-and-line-vertex-stroke-inactive',
                            type: 'circle',
                            filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
                            paint: {
                                'circle-radius': 5,
                                'circle-color': '#fff'
                            }
                        },
                        {
                            id: 'gl-draw-polygon-and-line-vertex-inactive',
                            type: 'circle',
                            filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
                            paint: {
                                'circle-radius': 3,
                                'circle-color': '#fbb03b'
                            }
                        },
                        {
                            id: 'gl-draw-point-point-stroke-inactive',
                            type: 'circle',
                            filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Point'], ['==', 'meta', 'feature'], ['!=', 'mode', 'static']],
                            paint: {
                                'circle-radius': 5,
                                'circle-opacity': 1,
                                'circle-color': '#fff'
                            }
                        },
                        {
                            id: 'gl-draw-point-inactive',
                            type: 'circle',
                            filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Point'], ['==', 'meta', 'feature'], ['!=', 'mode', 'static']],
                            paint: {
                                'circle-radius': 3,
                                'circle-color': '#3bb2d0'
                            }
                        },
                        {
                            id: 'gl-draw-point-stroke-active',
                            type: 'circle',
                            filter: ['all', ['==', '$type', 'Point'], ['==', 'active', 'true'], ['!=', 'meta', 'midpoint']],
                            paint: {
                                'circle-radius': 7,
                                'circle-color': '#fff'
                            }
                        },
                        {
                            id: 'gl-draw-point-active',
                            type: 'circle',
                            filter: ['all', ['==', '$type', 'Point'], ['!=', 'meta', 'midpoint'], ['==', 'active', 'true']],
                            paint: {
                                'circle-radius': 5,
                                'circle-color': '#fbb03b'
                            }
                        },
                        {
                            id: 'gl-draw-polygon-fill-static',
                            type: 'fill',
                            filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']],
                            paint: {
                                'fill-color': '#404040',
                                'fill-outline-color': '#404040',
                                'fill-opacity': 0.1
                            }
                        },
                        {
                            id: 'gl-draw-polygon-stroke-static',
                            type: 'line',
                            filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']],
                            layout: {
                                'line-cap': 'round',
                                'line-join': 'round'
                            },
                            paint: {
                                'line-color': '#404040',
                                'line-width': 2
                            }
                        },
                        {
                            id: 'gl-draw-line-static',
                            type: 'line',
                            filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'LineString']],
                            layout: {
                                'line-cap': 'round',
                                'line-join': 'round'
                            },
                            paint: {
                                'line-color': '#404040',
                                'line-width': 2
                            }
                        },
                        {
                            id: 'gl-draw-point-static',
                            type: 'circle',
                            filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Point']],
                            paint: {
                                'circle-radius': 5,
                                'circle-color': '#404040'
                            }
                        }
                    ]
                });

                if (this.allowDrawing || this.drawingGeoJSON) {
                    map.addControl(this.drawObject, 'top-left');

                    map.on('draw.create', this.emitDrawing);
                    map.on('draw.update', this.emitDrawing);
                    map.on('draw.delete', this.emitDrawing);
                }

                if (this.drawingGeoJSON) {
                    // console.log(JSON.stringify(this.drawingGeoJSON));
                    this.drawObject.set(this.drawingGeoJSON);
                }
            }

            // Click and move
            map.on('click', this.onMapClick);
            map.on('mousemove', this.onMouseMove);

            // Create a popup, but don't add it to the map yet.
            this.popup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false
            });

            // Default State
            this.renderParcels();
            this.renderMissingParcels();
            this.renderCentroids();
            this.fitBounds();   // default to this?
        },

        // Sample drawObject structure
        // {"type":"FeatureCollection","features":[{"id":"2ea91c730d70822a5c347e29a7d3ae61","type":"Feature","properties":{},"geometry":{"coordinates":[[-88.30251363671051,31.10997666812399],[-88.29427389061506,30.75186809028638],[-87.98665670311235,31.034696918008123],[-88.29152730858327,31.114679673069844]],"type":"LineString"}},{"id":"13e518a1aa917686194f5941f3b4702d","type":"Feature","properties":{},"geometry":{"coordinates":[[-87.46755269921839,31.084105976997165],[-87.26979879297006,31.478452262389638],[-87.13796285546718,31.084105976997165],[-87.4126210585942,31.055875371165342]],"type":"LineString"}}]}
        emitDrawing () {
            if (this.drawObject) {
                // console.log(JSON.stringify(this.drawObject.getAll()));
                this.$emit('drawing', this.drawObject.getAll());
            }
        },

        unsetMap () {
            this.map.off('draw.create', this.emitDrawing);
            this.map.off('draw.update', this.emitDrawing);
            this.map.off('draw.delete', this.emitDrawing);
            this.map.off('click', this.onMapClick);
            this.map.off('mousemove', this.onMouseMove);

            this.map.off('mouseenter', 'certpilot-highlighted', this.onMouseEnter_highlighted);
            this.map.off('mouseenter', 'missing-parcels', this.onMouseEnter_missing);
            this.map.off('mouseenter', 'woodland-centroids', this.onMouseEnter_centroids);

            this.map.off('mouseleave', 'certpilot-highlighted', this.onMouseLeave_highlighted);
            this.map.off('mouseleave', 'missing-parcels', this.onMouseLeave_missing);
            this.map.off('mouseleave', 'woodland-centroids', this.onMouseLeave_centroids);

            this.map.off('click', 'certpilot-highlighted', this.onMapClick_highlighted);
            this.map.off('click', 'missing-parcels', this.onMapClick_missing);
            this.map.off('click', 'woodland-centroids', this.onMapClick_centroids);

            this.map = null;
        },

        onMapClick (e) {
            try {
                if (this.map && (this.drawObject && this.drawObject.getFeatureIdsAt(e.point).length) || this.drawObject.getMode() != 'simple_select') {
                    // console.log("drawing, abort ... " + this.drawObject.getMode());
                    return;
                }
                this.$emit('mapclick', e);
            } catch (e) {

            }
        },

        onMouseMove (e) {
            if (!this.interactive) return;
            // If we are not on a draw object, highlight parcel if there is one,
            // also check for popup reqs for stands

            if (this.drawObject) {
                let featureIds = this.drawObject.getFeatureIdsAt(e.point);

                if (featureIds.length) {
                    if (featureIds[0] != this.overFeatureId) {
                        if (this.overFeatureId != '') {
                            this.popup.remove();
                        }

                        var drawFeature = this.drawObject.get(featureIds[0]);

                        if (drawFeature) {
                            var name = drawFeature.properties.name ? drawFeature.properties.name : 'unnamed';
                            var description = drawFeature.properties.notes ? drawFeature.properties.notes : '';
                            var area = (turfArea(drawFeature) / 4046.86).toFixed(2);

                            if (name || description || area) {
                                // Change the cursor style as a UI indicator.
                                // map.getCanvas().style.cursor = 'pointer';

                                // var coordinates = drawFeature.geometry.coordinates.slice();
                                // console.log(coordinates);

                                // // Ensure that if the map is zoomed out such that multiple
                                // // copies of the feature are visible, the popup appears
                                // // over the copy being pointed to.
                                // while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                                //     coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                                // }

                                // Populate the popup and set its coordinates
                                // based on the feature found.
                                this.popup
                                    .setLngLat(e.lngLat)
                                    .setHTML(name + ', ' + area + ' acres<p>' + description)
                                    .addTo(this.map);
                            }
                        }

                        this.overFeatureId = featureIds[0];
                    }
                } else {
                    this.overFeatureId = '';
                    this.popup.remove();
                }
            }

            if (!this.overFeatureId) {
                var features = this.map.queryRenderedFeatures(e.point, {
                    layers: ['certpilot']
                });

                // document.getElementById('features').innerHTML = JSON.stringify(features, null, 2);

                // Run through the selected features and set a filter
                // to match features with unique GID to activate
                // the `counties-highlighted` layer.
                var filter = features.reduce(
                    function (memo, feature) {
                        // console.log(JSON.stringify(feature.properties));
                        memo.push(feature.properties.id);
                        return memo;
                    },
                    ['in', 'id']
                );

                this.map.setFilter('certpilot-almost-highlighted', filter);
                //   console.log("here: " + this.drawObject)
            }
        },

        onMouseEnter_highlighted (e) {
            this.$emit('enter', e.features[0].properties);
        },

        onMouseEnter_missing (e) {
            this.$emit('enter', e.features[0].properties);
        },

        onMouseEnter_centroids (e) {
            this.$emit('enter', e.features[0].properties);
        },

        onMouseLeave_highlighted (e) {
            this.$emit('leave');//, e.features[0]);
        },

        onMouseLeave_missing (e) {
            this.$emit('leave');//, e.features[0]);
        },

        onMouseLeave_centroids (e) {
            this.$emit('leave');//, e.features[0]);
        },

        onMapClick_highlighted (e) {
            e.preventDefault();
            this.$emit('click', this.parcels.find((p) => { return p.id === e.features[0].properties.id; }));
        },
        onMapClick_missing (e) {
            e.preventDefault();
            this.$emit('click', this.missingParcels.find((p) => { return p.id === e.features[0].properties.id; }));
        },
        onMapClick_centroids (e) {
            e.preventDefault();
            this.$emit('click', this.centroids.find((p) => { return p.id === e.features[0].properties.id; }));
        },

        getMapInstance () {
            return new Promise((resolve, reject) => {
                resolve(this.map);
            });
        },

        resetMap () {
            return this.getMapInstance(map => {
                map.setCenter(this.center);
                map.setZoom(this.zoom);
            });
        },

        renderParcels () {
            if (this.parcels && this.map) {
                // Run through the selected parcels and use their
                // pid to filter selection layer
                var filter = this.parcels.reduce(
                    function (memo, parcel) {
                        memo.push(parcel.id);
                        return memo;
                    },
                    ['in', 'id']
                );

                this.map.setFilter('certpilot-highlighted', filter);
                this.map.setFilter('certpilot-highlighted-lines', filter);
            }
        },

        renderMissingParcels () {
            let features = [];

            if (this.map && this.missingParcels) {
                this.missingParcels.forEach(marker => {
                    features.push({
                        type: 'Feature',
                        properties: marker,
                        geometry: {
                            type: 'Point',
                            coordinates: [marker.position.lng, marker.position.lat]
                        }
                    });
                });

                this.map.getSource('missing-parcels').setData({
                    type: 'FeatureCollection',
                    features: features
                });
            }
        },

        renderCentroids () {
            let centroids = [];

            if (this.map && this.centroids) {
                this.centroids.forEach(marker => {
                    centroids.push({
                        type: 'Feature',
                        properties: marker,
                        geometry: {
                            type: 'Point',
                            coordinates: [marker.position.lng, marker.position.lat]
                        }
                    });
                });

                this.map.getSource('woodland-centroids').setData({
                    type: 'FeatureCollection',
                    features: centroids
                });
            }
        },

        fitBounds () {
            if (this.map &&
                ((this.parcels && this.parcels.length) || (this.missingParcels && this.missingParcels.length))) {
                let bounds = new mapboxgl.LngLatBounds();

                // Extend by parcel boundaries
                if (this.parcels) {
                    _.each(this.parcels, (parcel) => {
                        let bbox = turfBBox(JSON.parse(parcel.geom));

                        bounds.extend(new mapboxgl.LngLat(bbox[0], bbox[1]));
                        bounds.extend(new mapboxgl.LngLat(bbox[2], bbox[3]));
                    });
                }

                // Extend by missing parcel points
                if (this.missingParcels) {
                    _.each(this.missingParcels, (point) => {
                        bounds.extend(point.position);
                    });
                }

                this.map.fitBounds(bounds, { padding: 120, duration: 0 });
            } else {
                // console.error("map not ready for fit bounds");
            }
        },

        highlightMapFeatures (groupId = null) {
            //   this.getMapFeatures(groupId).forEach(feature => {
            //     feature.setOptions({ fillColor: "#754191", strokeColor: "#754191" });
            //   });
        },

        unhighlightMapFeatures (groupId = null) {
            //   this.getMapFeatures(groupId).forEach(feature => {
            //     feature.setOptions({ strokeColor: "#5ab74a", fillColor: "#5ab74a" });
            //   });
        },

        centerOnAddress (address) {
            let self = this;

            this.getMapInstance()
                .then(map => {
                    let res = address.match(/([+-]?([0-9]+([.][0-9]*)?|[.][0-9]+))\s*,\s*([+-]?([0-9]+([.][0-9]*)?|[.][0-9]+))/);

                    if (res) {
                        console.log(res);
                        map.setCenter(new mapboxgl.LngLat(res[4], res[1]));
                        map.setZoom(12);
                    } else {
                        if (self.geocoder && self.map) {
                            self.geocoder.query(address);
                        }
                    }
                })
                .catch(e => console.log(e));
        }
    }
};
</script>



// WEBPACK FOOTER //
// map.vue?1c3dba94